/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { ProgressTrackerStore } from 'src/core/stores/ProgressTrackerStore';
import { AuthoringRegion } from 'src/core/types/regionTypes';
import { getBackwardCompatibleRegion } from 'src/verticals/azureMigrationModal/MultiRegionMigrationModal';
import { AppToEmailStore } from 'src/verticals/azureMigrationModal/stores/AppToEmailStore';
import { AppToExportStore } from 'src/verticals/azureMigrationModal/stores/AppToExportStore';
import { MultiRegionMigrationSteps } from 'src/verticals/azureMigrationModal/types/azureMigrationModalTypes';

export type useMultiRegionMigrationStateMachineType = ReturnType<typeof useMultiRegionMigrationStateMachine>;
export type MigrationModalUserAction = 'previousScreen' | 'nextScreen' | 'createResource' | 'reset';

type MultiRegionMigrationStateMachineProps = {
    appsToEmail: AppToEmailStore[];
    appsToExport: AppToExportStore[];
    appsToEmailTracker: ProgressTrackerStore;
    appsToExportTracker: ProgressTrackerStore;
};

type MigrationModalState = {
    currentScreen: MultiRegionMigrationSteps;
    existsAppsToExportWithPredictionResource: boolean;
    existsAppsToExport: boolean;
    existsAppsToEmailWithPredictionResource: boolean;
    existsAppsToEmail: boolean;
};

export const getRegionScreen = (region: AuthoringRegion): MultiRegionMigrationSteps => {
    switch (region) {
        case 'westus':
            return MultiRegionMigrationSteps.chooseUsCsAccount;
        case 'westeurope':
            return MultiRegionMigrationSteps.chooseEuCsAccount;
        case 'australiaeast':
            return MultiRegionMigrationSteps.chooseAusCsAccount;
        default:
            return MultiRegionMigrationSteps.chooseCsAccount;
    }
};

/**
 * Contains function to get the next or previous screen/step of the
 * Migration modal based on the current screen loaded as well
 * as the UserStore and AzureStore state and the appsToEmail
 * and the appsToExport of the current migrating user
 */
export const useMultiRegionMigrationStateMachine = (props: MultiRegionMigrationStateMachineProps) => {
    const { appsToEmail, appsToExport, appsToEmailTracker, appsToExportTracker } = props;
    const { rootStore } = useGlobalState();
    const [currentScreen, setCurrentScreen] = React.useState<MultiRegionMigrationSteps>(MultiRegionMigrationSteps.start);

    const isInitializing = appsToEmailTracker.isRunning || appsToExportTracker.isRunning || rootStore.azureStore?.loadingState.isRunning;
    const migrationInfo = rootStore.userMigrationInfoStoreHouse.items;

    const azureMigrationReducer = (state: MigrationModalState, action: MigrationModalUserAction): MultiRegionMigrationSteps => {
        switch (state.currentScreen) {
            case MultiRegionMigrationSteps.start:
                return startScreenReducer(state);
            case MultiRegionMigrationSteps.emailCollaborators:
                return emailCollaboratorsScreenReducer(state, action);
            case MultiRegionMigrationSteps.exportApps:
                return exportAppsScreenReducer(state, action);
            case MultiRegionMigrationSteps.regionalMigration:
                return regionMigrationScreenReducer(state, action);
            case MultiRegionMigrationSteps.chooseCsAccount:
                return chooseAuthoringResourceScreenReducer(action);
            case MultiRegionMigrationSteps.createCsAccount:
                return createAuthoringResourceScreenReducer(action);
            case MultiRegionMigrationSteps.chooseUsCsAccount:
            case MultiRegionMigrationSteps.chooseEuCsAccount:
            case MultiRegionMigrationSteps.chooseAusCsAccount:
                return chooseRegionCsAccountScreenReducer(action);
            default:
                break;
        }
    };

    const startScreenReducer = (state: MigrationModalState): MultiRegionMigrationSteps => {
        if (state.existsAppsToEmail) {
            return MultiRegionMigrationSteps.emailCollaborators;
        }
        if (state.existsAppsToExport) {
            return MultiRegionMigrationSteps.exportApps;
        }
        return MultiRegionMigrationSteps.regionalMigration;
    };

    const emailCollaboratorsScreenReducer = (state: MigrationModalState, action: MigrationModalUserAction): MultiRegionMigrationSteps => {
        switch (action) {
            case 'nextScreen':
                return state.existsAppsToExport ? MultiRegionMigrationSteps.exportApps : MultiRegionMigrationSteps.regionalMigration;
            case 'previousScreen':
                return MultiRegionMigrationSteps.start;
            default:
                break;
        }
    };

    const exportAppsScreenReducer = (state: MigrationModalState, action: MigrationModalUserAction): MultiRegionMigrationSteps => {
        switch (action) {
            case 'nextScreen':
                return MultiRegionMigrationSteps.regionalMigration;
            case 'previousScreen':
                return state.existsAppsToEmail ? MultiRegionMigrationSteps.emailCollaborators : MultiRegionMigrationSteps.start;
            default:
                break;
        }
    };

    const regionMigrationScreenReducer = (state: MigrationModalState, action: MigrationModalUserAction): MultiRegionMigrationSteps => {
        const region = getBackwardCompatibleRegion();

        switch (action) {
            case 'nextScreen':
                if (region !== 'westus' && region !== 'centraluseuap') {
                    return MultiRegionMigrationSteps.chooseCsAccount;
                }

                return chooseRegionCsAccountScreenReducer(action);
            case 'previousScreen':
                if (state.existsAppsToExport) {
                    return MultiRegionMigrationSteps.exportApps;
                }
                if (state.existsAppsToEmail) {
                    return MultiRegionMigrationSteps.emailCollaborators;
                }
                return MultiRegionMigrationSteps.start;

            default:
                break;
        }
    };

    const chooseAuthoringResourceScreenReducer = (action: MigrationModalUserAction): MultiRegionMigrationSteps => {
        switch (action) {
            case 'nextScreen':
                return MultiRegionMigrationSteps.finish;
            case 'previousScreen':
                return MultiRegionMigrationSteps.regionalMigration;
            case 'createResource':
                return MultiRegionMigrationSteps.createCsAccount;
            default:
                break;
        }
    };

    const createAuthoringResourceScreenReducer = (action: MigrationModalUserAction): MultiRegionMigrationSteps => {
        switch (action) {
            case 'nextScreen':
                return MultiRegionMigrationSteps.finish;
            case 'previousScreen':
                return MultiRegionMigrationSteps.chooseCsAccount;
            default:
                break;
        }
    };

    const chooseRegionCsAccountScreenReducer = (action: MigrationModalUserAction) => {
        switch (action) {
            case 'nextScreen':
                const nextRegion = migrationInfo.find(item => !item.hasMigratedSuccessfully)?.region;

                return nextRegion ? getRegionScreen(nextRegion) : MultiRegionMigrationSteps.finish;
            case 'previousScreen':
                return MultiRegionMigrationSteps.regionalMigration;
            default:
        }
    };

    const getScreen = (action: MigrationModalUserAction) => {
        if (action === 'reset') {
            setCurrentScreen(MultiRegionMigrationSteps.start);
        } else {
            setCurrentScreen(
                azureMigrationReducer(
                    {
                        currentScreen,
                        existsAppsToEmail: appsToEmail?.length > 0,
                        existsAppsToExport: appsToExport?.length > 0,
                        existsAppsToEmailWithPredictionResource: appsToEmail?.some(a => a.hasPredictionResource === true),
                        existsAppsToExportWithPredictionResource: appsToExport?.some(a => a.hasPredictionResource === true)
                    },
                    action
                )
            );
        }
    };

    return { currentScreen, isInitializing, getScreen };
};
